import type { GencowCtx, GencowProcedureDef } from "@gencow/core";

export type LegacyScheduledMutation = {
  name?: string;
  argsSchema?: unknown;
  handler(ctx: GencowCtx, args: unknown): Promise<unknown>;
};

export type ScheduledActionMaps = {
  internalProcedureMap: Map<string, GencowProcedureDef<"internal", any, any, any>>;
  mutationProcedureMap: Map<string, GencowProcedureDef<"mutation", any, any, any>>;
  mutationMap: Map<string, LegacyScheduledMutation>;
};

export type ResolvedScheduledAction =
  | { kind: "internal"; name: string; def: GencowProcedureDef<"internal", any, any, any> }
  | { kind: "procedure.mutation"; name: string; def: GencowProcedureDef<"mutation", any, any, any> }
  | { kind: "legacy.mutation"; name: string; def: LegacyScheduledMutation };

export class ScheduledActionNotFoundError extends Error {
  status = 404;

  constructor(action: string) {
    super(`Scheduled action "${action}" not found`);
    this.name = "ScheduledActionNotFoundError";
  }
}

type ExecuteWithTimeout = (fn: () => Promise<unknown>, kind: "cron", name: string) => Promise<unknown>;
type ParseArgsFn = (schema: unknown, args: unknown) => unknown;

export function resolveWireAction(
  wireAction: string,
  actionTokenMap: Map<string, string>,
): string | undefined {
  return actionTokenMap.get(wireAction);
}

export function resolveScheduledAction(
  procedureName: string,
  maps: ScheduledActionMaps,
): ResolvedScheduledAction | undefined {
  const internal = maps.internalProcedureMap.get(procedureName);
  if (internal) return { kind: "internal", name: procedureName, def: internal };

  const procedureMutation = maps.mutationProcedureMap.get(procedureName);
  if (procedureMutation) return { kind: "procedure.mutation", name: procedureName, def: procedureMutation };

  const legacyMutation = maps.mutationMap.get(procedureName);
  if (legacyMutation) return { kind: "legacy.mutation", name: procedureName, def: legacyMutation };

  return undefined;
}

export function resolveScheduledProcedureName(params: {
  action: string;
  actionTokenMap?: Map<string, string>;
  maps: ScheduledActionMaps;
  allowWireActionNameFallback?: boolean;
}): string {
  const actionTokenMap = params.actionTokenMap;
  if (!actionTokenMap || actionTokenMap.size === 0) return params.action;

  const procedureName = resolveWireAction(params.action, actionTokenMap);
  if (procedureName) return procedureName;

  if (params.allowWireActionNameFallback === true && resolveScheduledAction(params.action, params.maps)) {
    return params.action;
  }

  throw new ScheduledActionNotFoundError(params.action);
}

async function flushRealtime(ctx: GencowCtx): Promise<void> {
  const realtime = ctx.realtime as any;
  if (realtime?._flushRefresh) {
    await realtime._flushRefresh();
  }
  if (realtime?._flushRealtimeEvents) {
    await realtime._flushRealtimeEvents();
  }
}

export async function executeScheduledAction(params: {
  action: string;
  rawArgs: unknown;
  actionTokenMap?: Map<string, string>;
  maps: ScheduledActionMaps;
  buildCtx: () => GencowCtx;
  parseArgs?: ParseArgsFn;
  executeWithTimeout: ExecuteWithTimeout;
  invoker: "cron" | "scheduler";
  allowWireActionNameFallback?: boolean;
}): Promise<unknown> {
  const procedureName = resolveScheduledProcedureName({
    action: params.action,
    actionTokenMap: params.actionTokenMap,
    maps: params.maps,
    allowWireActionNameFallback: params.allowWireActionNameFallback,
  });
  const resolved = resolveScheduledAction(procedureName, params.maps);
  if (!resolved) throw new ScheduledActionNotFoundError(procedureName);

  const ctx = params.buildCtx();
  const args = params.rawArgs ?? {};
  const input =
    resolved.kind === "legacy.mutation" && params.parseArgs
      ? params.parseArgs(resolved.def.argsSchema, args)
      : args;

  const result = await params.executeWithTimeout(
    () => resolved.def.handler(ctx, input as never),
    "cron",
    procedureName,
  );
  await flushRealtime(ctx);
  return result;
}

export function allResolvableProcedureNames(maps: ScheduledActionMaps): string[] {
  return [
    ...maps.internalProcedureMap.keys(),
    ...maps.mutationProcedureMap.keys(),
    ...maps.mutationMap.keys(),
  ];
}

export function allResolvableActionTokens(actionTokenMap: Map<string, string>): string[] {
  return [...actionTokenMap.keys()];
}
